import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  useAsset, useSections, useCreateSection, useEquipment,
  useAddSectionEquipment, useRemoveSectionEquipment, useUpdateAsset, useRetireAsset,
  useReorderSections, useReorderSectionItems,
} from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import { MoveButtons, reorder } from '../../components/MoveButtons.js';
import { OffRunControl } from '../../components/OffRun.js';
import { ASSET_TYPE_OPTIONS, assetTypeIcon, assetTypeLabel } from '../../lib/asset-types.js';
import type { Asset, AssetSection, AssetType } from '@keetcheck/shared';

// The sections endpoint aggregates equipment inline with flat equipmentName field
interface SectionEquipmentAgg {
  id: string;
  equipmentId: string;
  quantity: number;
  sortOrder: number;
  equipmentName?: string;
  equipmentType?: string;
  kind?: string;
  expected?: string | null;  // as written on the sheet, e.g. "8+ each"
}

/** Asset details with inline edit and retire (history on past runs is kept). */
function AssetInfoCard({ orgId, asset }: { orgId: string; asset: Asset }) {
  const nav = useNavigate();
  const updateMut = useUpdateAsset(orgId, asset.id);
  const retireMut = useRetireAsset(orgId);
  const [editing, setEditing] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [name, setName] = useState(asset.name);
  const [assetType, setAssetType] = useState(asset.assetType);
  const [registration, setRegistration] = useState(asset.registration ?? '');
  const [notes, setNotes] = useState(asset.notes ?? '');
  const [primaryResponse, setPrimaryResponse] = useState(!!asset.primaryResponse);

  const card: React.CSSProperties = {
    margin: '14px 16px', padding: '14px 16px',
    background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12,
  };

  if (!editing) {
    return (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name={assetTypeIcon(asset.assetType)} size={22} style={{ color: theme.textDim, flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 18, fontWeight: 700, color: theme.text }}>{asset.name}</div>
          <button aria-label="Edit asset" onClick={() => setEditing(true)} style={iconBtnSmall} title="Edit asset">
            <Icon name="edit" size={13} />
          </button>
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
          {[assetTypeLabel(asset.assetType), asset.registration, asset.primaryResponse ? 'Primary response vehicle' : null].filter(Boolean).join(' · ')}
        </div>
        {asset.notes && (
          <div style={{ fontSize: 13, color: theme.textDim, marginTop: 8 }}>{asset.notes}</div>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        updateMut.mutate(
          {
            name: name.trim(),
            assetType,
            registration: registration.trim() || null,
            notes: notes.trim() || null,
            primaryResponse,
          },
          { onSuccess: () => setEditing(false) },
        );
      }}
      style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <input required value={name} onChange={(e) => setName(e.target.value)} aria-label="Name"
        placeholder="Name" style={inputStyle} />
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={assetType} onChange={(e) => setAssetType(e.target.value as AssetType)} aria-label="Type"
          style={{ ...inputStyle, flex: 1 }}>
          {ASSET_TYPE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <input value={registration} onChange={(e) => setRegistration(e.target.value)} aria-label="Registration"
          placeholder="Registration" style={{ ...inputStyle, flex: 1 }} />
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Notes"
        placeholder="Notes" rows={2} style={{ ...inputStyle, resize: 'none' }} />
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: theme.text, cursor: 'pointer' }}>
        <input type="checkbox" checked={primaryResponse} onChange={(e) => setPrimaryResponse(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          Primary response vehicle
          <span style={{ display: 'block', fontSize: 12, color: theme.textMuted }}>
            The one crews use first. If it goes off run, everyone is told to use the next available {assetTypeLabel(assetType).toLowerCase().replace('other', 'vehicle')} in your list. A unit has one.
          </span>
        </span>
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setEditing(false)} style={{ ...inputStyle, cursor: 'pointer', flex: 1 }}>
          Cancel
        </button>
        <button type="submit" disabled={updateMut.isPending} style={{ ...addBtnSmall, flex: 2, justifyContent: 'center' }}>
          {updateMut.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {confirmRetire ? (
        <div style={{
          padding: 10, borderRadius: 10, fontSize: 12, lineHeight: 1.4,
          background: theme.status.danger.bg, color: theme.status.danger.fg,
        }}>
          Retiring removes it from lists and from future scheduled runs; past inspections stay on record.
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => setConfirmRetire(false)} style={{ ...iconBtnSmall, padding: '0 10px' }}>Keep</button>
            <button type="button"
              onClick={() => retireMut.mutate(asset.id, { onSuccess: () => nav('/admin/assets', { replace: true }) })}
              style={{ ...iconBtnSmall, background: theme.status.danger.dot, color: '#fff', border: 'none', width: 'auto', padding: '0 12px' }}>
              Retire asset
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirmRetire(true)} style={{
          alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: theme.status.danger.fg, fontSize: 12, fontWeight: 600, textDecoration: 'underline',
          fontFamily: theme.font,
        }}>
          Retire asset
        </button>
      )}
    </form>
  );
}

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const nav = useNavigate();
  const { data: asset, isLoading } = useAsset(orgId, assetId ?? '');
  const { data: sections = [] } = useSections(orgId, assetId ?? '');
  const createSectionMut = useCreateSection(orgId, assetId ?? '');
  const reorderSectionsMut = useReorderSections(orgId, assetId ?? '');
  const { data: equipmentList = [] } = useEquipment(orgId);

  const [newSectionName, setNewSectionName] = useState('');
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const tree = sectionTree(sections);
  // A row shows only while every section above it is expanded.
  const visible = tree.filter((r) => r.ancestors.every((id) => openSections.has(id)));
  const allOpen = tree.length > 0 && tree.every((r) => openSections.has(r.section.id));

  function toggleSection(id: string) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleAddSection(e: React.FormEvent) {
    e.preventDefault();
    if (!newSectionName.trim()) return;
    createSectionMut.mutate(
      { name: newSectionName.trim() },
      { onSuccess: () => { setNewSectionName(''); setShowSectionForm(false); } },
    );
  }

  if (isLoading) return <EmptyMsg>Loading…</EmptyMsg>;
  if (!asset) return <EmptyMsg>Asset not found</EmptyMsg>;

  return (
    <div style={{ flex: 1 }}>
      <AssetInfoCard orgId={orgId} asset={asset} />
      <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <OffRunControl orgId={orgId} assetId={asset.id} assetName={asset.name} offRun={asset.offRun ?? null} isAdmin />
        <button onClick={() => nav(`/faults?asset=${asset.id}`)} style={{
          background: 'none', border: 'none', padding: 0, color: theme.accent, fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: theme.font, textAlign: 'left',
        }}>Faults on {asset.name} →</button>
      </div>

      {/* sections toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: `1px solid ${theme.border}`,
        borderBottom: `1px solid ${theme.border}`,
        background: theme.bg,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>
          Sections ({sections.length})
        </span>
        {tree.length > 0 && (
          <button
            onClick={() => setOpenSections(allOpen ? new Set() : new Set(tree.map((r) => r.section.id)))}
            style={{ ...addBtnSmall, background: theme.surface2, color: theme.textDim, border: `1px solid ${theme.border}`, marginLeft: 'auto', marginRight: 8 }}
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
        <button onClick={() => setShowSectionForm(!showSectionForm)} style={addBtnSmall}>
          <Icon name="plus" size={14} /> Add section
        </button>
      </div>

      {showSectionForm && (
        <form onSubmit={handleAddSection} style={{
          padding: '12px 16px', background: theme.surface2,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', gap: 8,
        }}>
          <input
            required value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            placeholder="Section name (e.g. Cabin)"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button type="submit" disabled={createSectionMut.isPending} style={smallSubmit}>
            {createSectionMut.isPending ? '…' : 'Add'}
          </button>
        </form>
      )}

      {sections.length === 0 && <EmptyMsg>No sections yet.</EmptyMsg>}

      {visible.map(({ section: sec, depth, childCount, totalItems, siblings, siblingIndex }) => (
        <SectionPanel
          key={sec.id}
          move={
            <MoveButtons
              disabled={reorderSectionsMut.isPending}
              onUp={siblingIndex > 0
                ? () => reorderSectionsMut.mutate(reorder(siblings, siblingIndex, -1)) : undefined}
              onDown={siblingIndex < siblings.length - 1
                ? () => reorderSectionsMut.mutate(reorder(siblings, siblingIndex, 1)) : undefined}
            />
          }
          section={sec}
          depth={depth}
          childCount={childCount}
          totalItems={totalItems}
          orgId={orgId}
          assetId={assetId ?? ''}
          equipmentList={equipmentList}
          open={openSections.has(sec.id)}
          onToggle={() => toggleSection(sec.id)}
        />
      ))}
    </div>
  );
}

/**
 * Sections as the sheets lay them out: depth-first, siblings by sort order,
 * with their nesting depth (sheet › locker › tray…). The API returns them
 * flat and sibling sort orders repeat across levels, so order must come
 * from the tree, not a flat sort.
 */
interface TreeRow {
  section: AssetSection;
  depth: number;
  ancestors: string[];   // ids from the top section down to the parent
  childCount: number;    // direct sub-sections
  totalItems: number;    // equipment in this section and everything inside it
  siblings: AssetSection[];  // same parent, in order (for ▲▼)
  siblingIndex: number;
}

function sectionTree(sections: AssetSection[]): TreeRow[] {
  const kids = new Map<string | null, AssetSection[]>();
  for (const s of sections) {
    const key = s.parentSectionId ?? null;
    kids.set(key, [...(kids.get(key) ?? []), s]);
  }
  const out: TreeRow[] = [];
  const walk = (parent: string | null, ancestors: string[]): number => {
    let sum = 0;
    const siblings = (kids.get(parent) ?? []).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    for (const [siblingIndex, s] of siblings.entries()) {
      const row: TreeRow = {
        section: s, depth: ancestors.length, ancestors,
        childCount: kids.get(s.id)?.length ?? 0, totalItems: 0,
        siblings, siblingIndex,
      };
      out.push(row);
      row.totalItems = (s.equipment?.length ?? 0) + walk(s.id, [...ancestors, s.id]);
      sum += row.totalItems;
    }
    return sum;
  };
  walk(null, []);
  return out;
}

function SectionPanel({
  section, depth, childCount, totalItems, orgId, assetId, equipmentList, open, onToggle, move,
}: {
  move: React.ReactNode;
  section: AssetSection;
  depth: number;
  childCount: number;
  totalItems: number;
  orgId: string;
  assetId: string;
  equipmentList: { id: string; name: string }[];
  open: boolean;
  onToggle: () => void;
}) {
  const addMut = useAddSectionEquipment(orgId, assetId, section.id);
  const removeMut = useRemoveSectionEquipment(orgId, assetId, section.id);
  const reorderItemsMut = useReorderSectionItems(orgId, assetId, section.id);
  const addChildMut = useCreateSection(orgId, assetId);

  const [selectedEquipId, setSelectedEquipId] = useState('');
  const [qty, setQty] = useState('1');
  const [showEquipForm, setShowEquipForm] = useState(false);
  const [childName, setChildName] = useState('');
  const [showChildForm, setShowChildForm] = useState(false);

  function handleAddEquip(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedEquipId) return;
    addMut.mutate(
      { equipmentId: selectedEquipId, quantity: parseInt(qty, 10) || 1 },
      { onSuccess: () => { setSelectedEquipId(''); setQty('1'); setShowEquipForm(false); } },
    );
  }

  const assignments = (section.equipment ?? []) as unknown as SectionEquipmentAgg[];

  return (
    <div style={{ borderBottom: `1px solid ${theme.border}` }}>
      {/* section header row */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: `12px 16px 12px ${16 + depth * 22}px`, cursor: 'pointer',
          background: depth === 0 ? theme.surface : theme.bg,
          borderLeft: depth ? `3px solid ${theme.border}` : 'none',
        }}
      >
        <Icon name={open ? 'chevronD' : 'chevron'} size={15} style={{ color: theme.textMuted }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: depth === 0 ? 15 : 14, fontWeight: depth === 0 ? 700 : 600, color: theme.text }}>
            {section.name}
          </span>
          <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>
            {[
              childCount ? `${childCount} inside` : null,
              `${totalItems} item${totalItems !== 1 ? 's' : ''}`,
            ].filter(Boolean).join(' · ')}
          </span>
        </div>
        {move}
        <button
          onClick={(e) => { e.stopPropagation(); setShowChildForm(!showChildForm); }}
          title="Add a sub-section (e.g. a tray inside this locker)"
          style={{ ...addBtnSmall, background: theme.surface2, color: theme.textDim, border: `1px solid ${theme.border}` }}
        >
          + Sub
        </button>
        <button aria-label="Add equipment to this section"
          onClick={(e) => { e.stopPropagation(); setShowEquipForm(!showEquipForm); if (!open) onToggle(); }}
          title="Add equipment to this section"
          style={addBtnSmall}
        >
          <Icon name="plus" size={13} />
        </button>
      </div>

      {showChildForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!childName.trim()) return;
            addChildMut.mutate(
              { name: childName.trim(), parentSectionId: section.id },
              { onSuccess: () => { setChildName(''); setShowChildForm(false); } },
            );
          }}
          style={{
            display: 'flex', gap: 8, padding: `8px 16px 8px ${38 + depth * 22}px`,
            background: theme.surface2, borderTop: `1px solid ${theme.border}`,
          }}
        >
          <input autoFocus value={childName} onChange={(e) => setChildName(e.target.value)}
            placeholder={`Inside “${section.name}” (e.g. Tray 1)`} aria-label="Sub-section name"
            style={{ ...inputStyle, flex: 1 }} />
          <button type="submit" disabled={addChildMut.isPending} style={smallSubmit}>Add</button>
        </form>
      )}

      {open && (
        <div style={{ background: theme.surface2 }}>
          {showEquipForm && (
            <form onSubmit={handleAddEquip} style={{
              padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center',
              borderBottom: `1px solid ${theme.border}`,
            }}>
              <select
                value={selectedEquipId}
                onChange={(e) => setSelectedEquipId(e.target.value)}
                required style={{ ...inputStyle, flex: 1 }}
              >
                <option value="">Select equipment…</option>
                {equipmentList.map((eq) => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </select>
              <input
                type="number" min={1} value={qty}
                onChange={(e) => setQty(e.target.value)}
                style={{ ...inputStyle, width: 60 }}
              />
              <button type="submit" disabled={addMut.isPending} style={smallSubmit}>
                {addMut.isPending ? '…' : 'Add'}
              </button>
            </form>
          )}

          {assignments.length === 0 && childCount === 0 && (
            <div style={{ padding: `14px 20px 14px ${38 + depth * 22}px`, fontSize: 13, color: theme.textMuted }}>
              No equipment assigned.
            </div>
          )}

          {assignments.map((a, i) => (
            <div
              key={a.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: `10px 20px 10px ${38 + depth * 22}px`,
                borderBottom: i < assignments.length - 1 ? `1px solid ${theme.border}` : 'none',
              }}
            >
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, color: theme.text, fontWeight: 500 }}>
                  {a.equipmentName ?? 'Item'}
                </span>
                {a.expected
                  ? <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>{a.expected}</span>
                  : a.quantity > 1 && (
                    <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>× {a.quantity}</span>
                  )}
              </div>
              <MoveButtons
                disabled={reorderItemsMut.isPending}
                onUp={i > 0 ? () => reorderItemsMut.mutate(reorder(assignments, i, -1)) : undefined}
                onDown={i < assignments.length - 1 ? () => reorderItemsMut.mutate(reorder(assignments, i, 1)) : undefined}
              />
              <button aria-label="Remove from this section" title="Remove from this section"
                onClick={() => removeMut.mutate(a.id)}
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  border: `1px solid ${theme.border}`,
                  background: theme.surface, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: theme.textMuted,
                }}
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 8,
  fontSize: 13, color: theme.text, fontFamily: theme.font,
  outline: 'none', boxSizing: 'border-box',
};

const iconBtnSmall: React.CSSProperties = {
  minWidth: 28, height: 28, borderRadius: 6, flexShrink: 0,
  border: `1px solid ${theme.border}`, background: theme.surface2,
  color: theme.textDim, cursor: 'pointer', fontFamily: theme.font, fontSize: 12, fontWeight: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const addBtnSmall: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const smallSubmit: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font, flexShrink: 0,
};
